import type { CmsRichTextDocument, CmsRichTextNode } from './models/content';

const escapeHtml = (value: string) => value.replace(/[&<>'"]/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' })[char]!);
const safeUrl = (value: unknown) => typeof value === 'string' && (/^https:\/\//.test(value) || /^mailto:/.test(value) || value.startsWith('/') || value.startsWith('#')) ? value : null;
const text = (node?: CmsRichTextNode): string => (node?.content || []).map((item) => item.text || text(item)).join('');

function marks(value: string, node: CmsRichTextNode): string {
  return (node.marks || []).reduce((html, mark) => {
    if (mark.type === 'bold') return `<strong>${html}</strong>`;
    if (mark.type === 'italic') return `<em>${html}</em>`;
    if (mark.type === 'underline') return `<u>${html}</u>`;
    if (mark.type === 'strike') return `<s>${html}</s>`;
    if (mark.type === 'code') return `<code>${html}</code>`;
    if (mark.type === 'link') { const href = safeUrl(mark.attrs?.href); return href ? `<a href="${escapeHtml(href)}" rel="noopener noreferrer">${html}</a>` : html; }
    return html;
  }, value);
}

function nodeHtml(node: CmsRichTextNode): string {
  const children = (node.content || []).map(nodeHtml).join('');
  if (node.type === 'text') return marks(escapeHtml(node.text || ''), node);
  if (node.type === 'hardBreak') return '<br>';
  if (node.type === 'paragraph') return `<p>${children}</p>`;
  if (node.type === 'heading') { const level = Number(node.attrs?.level); return `<h${level >= 1 && level <= 3 ? level : 2}>${children}</h${level >= 1 && level <= 3 ? level : 2}>`; }
  if (node.type === 'bulletList') return `<ul>${children}</ul>`;
  if (node.type === 'orderedList') return `<ol>${children}</ol>`;
  if (node.type === 'listItem') return `<li>${children}</li>`;
  if (node.type === 'blockquote') return `<blockquote>${children}</blockquote>`;
  if (node.type === 'codeBlock') return `<pre><code>${children}</code></pre>`;
  if (node.type === 'table') return `<table><tbody>${children}</tbody></table>`;
  if (node.type === 'tableRow') return `<tr>${children}</tr>`;
  if (node.type === 'tableHeader') return `<th>${children}</th>`;
  if (node.type === 'tableCell') return `<td>${children}</td>`;
  if (node.type === 'horizontalRule') return '<hr>';
  if (node.type === 'image') { const src = safeUrl(node.attrs?.src); return src ? `<img src="${escapeHtml(src)}" alt="${escapeHtml(typeof node.attrs?.alt === 'string' ? node.attrs.alt : '')}">` : ''; }
  return children;
}

export function cmsRichTextHtml(document: CmsRichTextDocument): string {
  return document?.type === 'doc' && Array.isArray(document.content) ? document.content.map(nodeHtml).join('') : '';
}

export function cmsRichTextText(document: CmsRichTextDocument): string {
  return document?.type === 'doc' && Array.isArray(document.content) ? text({ type: 'doc', content: document.content }) : '';
}
